import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseQueryOptions, useQuery, InfiniteData, useQueryClient } from 'react-query'
import { BrandPlanSchedule } from 'types'
import { scheduleKeys } from './scheduleKeys'
import { TransformedBrandPlanSchedule } from 'screens/BrandKits/components/BrandPlanTimeline/Types'
import { transformSchedule } from 'screens/BrandKits/components/BrandPlanTimeline/useTransformedBrandPlanSchedules'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { QueryKey } from 'react-query/types/core/types'

export type Data = BrandPlanSchedule

export interface Options<TQueryFnData extends Data = Data, TData extends Data = Data>
  extends UseQueryOptions<TQueryFnData, unknown, TData> {}

export type CacheData<T extends BrandPlanSchedule = BrandPlanSchedule> =
  | InfiniteData<{ data: { schedules: T[] } }>
  | undefined

export const findScheduleInCache = <T extends BrandPlanSchedule = BrandPlanSchedule>(
  cache: [QueryKey, CacheData<T>][],
  scheduleId: number,
): TransformedBrandPlanSchedule<T> | undefined => {
  if (!cache) return undefined

  for (const [, data] of cache) {
    if (!data) continue

    for (const page of data.pages) {
      const match = page.data.schedules.find((s) => s.id === scheduleId)
      if (match) return transformSchedule(match)
    }
  }

  return undefined
}

export const fetchBrandPlanSchedule = async <T extends Data = Data>({
  id,
  signal,
}: {
  id: number
  signal?: AbortSignal
}) => {
  const res = await sendToApi<{ schedule: T }>(`/schedules/${id}`, { signal })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res.data.schedule
}

export const useBrandPlanSchedule = <TQueryFnData extends Data = Data, TData extends Data = Data>(
  id: number,
  options?: Options<TQueryFnData, TData>,
) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const cacheData = queryClient.getQueriesData<CacheData<TQueryFnData>>([
    'workspaces',
    workspaceId,
    'schedules',
    'list',
  ])
  return useQuery<TQueryFnData, unknown, TData>(
    scheduleKeys.detail(id),
    () => fetchBrandPlanSchedule<TQueryFnData>({ id }),
    {
      refetchOnWindowFocus: false,
      placeholderData: () => findScheduleInCache<TQueryFnData>(cacheData, id),
      ...options,
    },
  )
}
